import React, { useState, useEffect } from 'react';
import HttpService from '../../service/HttpService';
import Table from './Table';

const UsersSection = () => {
    const { axiosInstance } = HttpService();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false); // Track edit modal visibility
    const [selectedUser, setSelectedUser] = useState(null); // Store the user being edited
    const [showCreateModal, setShowCreateModal] = useState(false); // Track create modal visibility
    const [loadingEdit, setLoadingEdit] = useState(false); // Track loading state for edit
    const [loadingCreate, setLoadingCreate] = useState(false); // Track loading state for create
    const [errorModal, setErrorModal] = useState({ show: false, message: '' }); // Track error modal visibility and message
    const [showDeleteModal, setShowDeleteModal] = useState(false); // Track delete modal visibility
    const [loadingDelete, setLoadingDelete] = useState(false); // Track loading state for delete
    const [successModal, setSuccessModal] = useState({ show: false, link: '' }); // Success modal for reset password link
    const [licenses, setLicenses] = useState([]); // Store licenses fetched from the API


    const [newUser, setNewUser] = useState({
        email: '',
        displayName: '',
        profile: 'USER',
        password: '',
        licenseHash: '',
    });

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchLicenses = async () => {
        try {
            const response = await axiosInstance.get('/licenses');
            setLicenses(response.data); // Store the licenses in state
        } catch (error) {
            console.error('Failed to fetch licenses:', error);
        }
    };

    const handleCreateUser = async () => {
        if (!newUser.email || !newUser.displayName || !newUser.password || !newUser.licenseHash) {
            setErrorModal({ show: true, message: 'All fields are required.' });
            return;
        }

        if (!/\S+@\S+\.\S+/.test(newUser.email)) {
            setErrorModal({ show: true, message: 'Please enter a valid email address.' });
            return;
        }

        setLoadingCreate(true);
        try {
            await axiosInstance.post('/users/create', {
                email: newUser.email,
                displayName: newUser.displayName,
                profiles: [newUser.profile],
                licenseHash: newUser.licenseHash,
                password: newUser.password,
            });
            setShowCreateModal(false);
            fetchUsers(); // Reload the user list
        } catch (error) {
            console.error('Failed to create user:', error);
            const errorMessage =
                error.response?.data?.detail || 'An unexpected error occurred.';
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setLoadingCreate(false);
        }
    };

    const handleEditUser = async () => {
        if (!selectedUser) return;

        setLoadingEdit(true);
        try {
            await axiosInstance.post(`/users/edit/${selectedUser.userId}`, {
                displayName: selectedUser.displayName,
                profiles: selectedUser.profiles,
                disabled: selectedUser.disabled,
            });
            setShowEditModal(false);
            fetchUsers(); // Reload the user list after editing
        } catch (error) {
            console.error('Failed to edit user:', error);
            const errorMessage =
                error.response?.data?.detail || 'An unexpected error occurred.';
            setErrorModal({ show: true, message: errorMessage }); // Show error modal
        } finally {
            setLoadingEdit(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        setLoadingDelete(true);
        try {
            await axiosInstance.delete(`/users/delete/${selectedUser.userId}`);
            setShowDeleteModal(false);
            fetchUsers(); // Reload the user list after deletion
        } catch (error) {
            console.error('Failed to delete user:', error);
            const errorMessage =
                error.response?.data?.detail || 'An unexpected error occurred.';
            setErrorModal({ show: true, message: errorMessage }); // Show error modal
        } finally {
            setLoadingDelete(false);
        }
    };

    const columns = [
        { key: 'displayName', label: 'Name' },
        { key: 'email', label: 'Email' },
        {
            key: 'profiles',
            label: 'Profiles',
            render: (profiles) => profiles.join(', '),
        },
        {
            key: 'disabled',
            label: 'Status',
            render: (disabled) =>
                disabled ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        Disabled
                    </span>
                ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                    </span>
                ),
        },
        { key: 'licenseName', label: 'License Name' },
    ];

    const actions = [
        {
            label: 'Edit',
            className:
                'bg-blue-100 text-blue-600 px-3 py-1 rounded-lg hover:bg-blue-200 transition',
            onClick: (user) => {
                setSelectedUser({ ...user }); // Clone the user object to avoid direct mutation
                setShowEditModal(true);
            },
        },
        {
            label: 'Delete',
            className:
                'bg-red-100 text-red-600 px-3 py-1 rounded-lg hover:bg-red-200 transition',
            onClick: (user) => {
                setSelectedUser(user);
                setShowDeleteModal(true);
            },
        },
    ];

    // Open the create modal
    const openCreateModal = () => {
        setNewUser({
            email: '',
            displayName: '',
            profile: 'USER',
            password: '',
            licenseHash: '',
        }); // Reset the fields
        setShowCreateModal(true); // Open the modal
    };

    useEffect(() => {
        fetchUsers();
        fetchLicenses(); // Fetch licenses when the component loads
    }, []);

    return (
        <section id="usersSection" className="dashboard-section">
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-800">Manage Users</h2>
                    <button
                        id="addUserBtn"
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center"
                        onClick={() => openCreateModal()}
                    >
                        <i className="fas fa-plus mr-2"></i> Add User
                    </button>
                </div>
                {isLoading ? (
                    <div className="text-center">Loading...</div>
                ) : (
                    <Table columns={columns} data={users} actions={actions} />
                )}
            </div>

            {/* Create User Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Create User</h3>
                        <form>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">Email</label>
                                <input
                                    type="email"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={newUser.email}
                                    autoComplete="off"
                                    onChange={(e) =>
                                        setNewUser({ ...newUser, email: e.target.value })
                                    }
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Display Name
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={newUser.displayName}
                                    autoComplete="off"
                                    onChange={(e) =>
                                        setNewUser({ ...newUser, displayName: e.target.value })
                                    }
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">Profile</label>
                                <select
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={newUser.profile}
                                    onChange={(e) =>
                                        setNewUser({ ...newUser, profile: e.target.value })
                                    }
                                >
                                    <option value="ADMIN">ADMIN</option>
                                    <option value="USER">USER</option>
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">Password</label>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={newUser.password}
                                    onChange={(e) =>
                                        setNewUser({ ...newUser, password: e.target.value })
                                    }
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">License</label>
                                <select
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={newUser.licenseHash}
                                    onChange={(e) =>
                                        setNewUser({ ...newUser, licenseHash: e.target.value })
                                    }
                                >
                                    <option value="">Select License</option>
                                    {licenses.map((license) => (
                                        <option key={license.licenseHash} value={license.licenseHash}>
                                            {license.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex justify-between">
                                <button
                                    type="button"
                                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                                    onClick={handleCreateUser}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {showEditModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit User</h3>
                        <form>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Display Name
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={selectedUser.displayName}
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            displayName: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Profile
                                </label>
                                <select
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={selectedUser.profiles[0]} // Assuming single profile for simplicity
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            profiles: [e.target.value],
                                        })
                                    }
                                >
                                    <option value="GOD">GOD</option>
                                    <option value="ADMIN">ADMIN</option>
                                    <option value="USER">USER</option>
                                </select>
                            </div>
                            <div className="mb-4 flex items-center">
                                <input
                                    type="checkbox"
                                    id="disabled"
                                    className="mr-2"
                                    checked={selectedUser.disabled}
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            disabled: e.target.checked,
                                        })
                                    }
                                />
                                <label htmlFor="disabled" className="text-gray-700 font-medium">
                                    Disabled
                                </label>
                            </div>
                            <div className="flex justify-between">
                                <button
                                    type="button"
                                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={loadingEdit}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                                    onClick={handleEditUser}
                                    disabled={loadingEdit}
                                >
                                    {loadingEdit ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md text-center">
                        <h3 className="text-lg font-semibold text-red-600 mb-4">Confirm Deletion</h3>
                        <p className="text-gray-700 mb-4">
                            Are you sure you want to delete the user{' '}
                            <strong>{selectedUser.displayName}</strong>?
                        </p>
                        <div className="flex justify-between">
                            <button
                                type="button"
                                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={loadingDelete}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                                onClick={handleDeleteUser}
                                disabled={loadingDelete}
                            >
                                {loadingDelete ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {(loadingEdit || loadingDelete || loadingCreate) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin"></div>
                </div>
            )}

            {/* Success Modal */}
            {successModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md text-center">
                        <h3 className="text-lg font-semibold text-green-600 mb-4">User Created</h3>
                        <p className="text-gray-700 mb-4">
                            Reset password link: 
                            <p
                                className="text-blue-600 cursor-pointer"
                                href={successModal.link} // Show full link on hover
                            >
                                {successModal.link.length > 30
                                    ? `${successModal.link.substring(0, 30)}...` // Truncate the link
                                    : successModal.link}
                            </p>
                        </p>
                        <div className="flex justify-between gap-4 mt-4">
                            <button
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                                onClick={() => navigator.clipboard.writeText(successModal.link)}
                            >
                                Copy Link
                            </button>
                            <button
                                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
                                onClick={() => setSuccessModal({ show: false, link: '' })}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

           {/* Error Modal */}
           {errorModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md text-center">
                        <h3 className="text-lg font-semibold text-red-600 mb-4">Error</h3>
                        <p className="text-gray-700 mb-4">{errorModal.message}</p>
                        <button
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                            onClick={() => setErrorModal({ show: false, message: '' })}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default UsersSection;